import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import employee components (lazy-loaded for performance)
const EmployeesList = React.lazy(() => import("./EmployeesList"));
const NewEmployee = React.lazy(() => import("./NewEmployee"));
const EmployeeDetails = React.lazy(() => import("./EmployeeDetails"));
const BulkUploadEmployees = React.lazy(() => import("./BulkUploadEmployees"));
const EmployeeRoles = React.lazy(() => import("./EmployeeRoles"));
const EmployeePositions = React.lazy(() => import("./EmployeePositions"));
const AwardClassifications = React.lazy(() => import("./AwardClassifications"));
const Departments = React.lazy(() => import("./Departments"));
const Teams = React.lazy(() => import("./Teams"));
const EmployeeInvites = React.lazy(() => import("./EmployeeInvites"));
const MyDetails = React.lazy(() => import("./MyDetails"));
const IncidentNotificationRules = React.lazy(() => import("./IncidentNotificationRules"));
const NotificationRules = React.lazy(() => import("./NotificationRules"));

const employeesModuleConfig: ModuleConfig = {
    // Module Identity
    id: "employees",
    name: "Employee Management",
    description: "Manage employee records, HR information, and workforce data",
    version: "1.0.0",
    enabled: true,
    category: "hr",
    order: 3,
    icon: "👷",
    color: "#5A67D8",

    // Routes
    // Permissions are enforced server-side via employeesPermissions in the backend.
    // permissionLevel indicates which backend permission level is required.
    routes: [
        {
            path: "/employees/my-details",
            component: MyDetails,
            permissions: [],
            permissionLevel: "self",
            useLayout: true,
        },
        {
            path: "/employees",
            component: EmployeesList,
            permissions: [],
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/employees/new",
            component: NewEmployee,
            permissions: [],
            permissionLevel: "create",
            useLayout: true,
        },
        {
            path: "/employees/invites",
            component: EmployeeInvites,
            permissions: [],
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/employees/notification-rules",
            component: NotificationRules,
            permissions: [],
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/employees/incident-notification-rules",
            component: IncidentNotificationRules,
            permissions: [],
            permissionLevel: "delete",
            useLayout: true,
        },
        {
            path: "/employees/:id",
            component: EmployeeDetails,
            permissions: [],
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/employees/edit/:id",
            component: NewEmployee, // Reuse NewEmployee component for editing
            permissions: [],
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/employees/bulk-upload",
            component: BulkUploadEmployees,
            permissions: [],
            permissionLevel: "create",
            useLayout: true,
        },
        {
            path: "/employees/roles",
            component: EmployeeRoles,
            permissions: [],
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/employees/positions",
            component: EmployeePositions,
            permissions: [],
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/employees/award-classifications",
            component: AwardClassifications,
            permissions: [],
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/employees/departments",
            component: Departments,
            permissions: [],
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/employees/teams",
            component: Teams,
            permissions: [],
            permissionLevel: "edit",
            useLayout: true,
        }
    ],

    // Navigation
    navigation: [
        {
            label: "My Details",
            path: "/employees/my-details",
            icon: "👤",
            permissions: [],
            permissionLevel: "self",
        },
        {
            label: "All Employees",
            path: "/employees",
            icon: "👥",
            permissions: [],
            permissionLevel: "view",
        },
        {
            label: "Add Employee",
            path: "/employees/new",
            icon: "➕",
            permissions: [],
            permissionLevel: "create",
        },
        {
            label: "Bulk Upload",
            path: "/employees/bulk-upload",
            icon: "📤",
            permissions: [],
            permissionLevel: "create",
        },
        {
            label: "Invites",
            path: "/employees/invites",
            icon: "📧",
            permissions: [],
            permissionLevel: "view",
        },
        {
            label: "Manage Roles",
            path: "/employees/roles",
            icon: "🏷️",
            permissions: [],
            permissionLevel: "edit",
        },
        {
            label: "Manage Positions",
            path: "/employees/positions",
            icon: "💼",
            permissions: [],
            permissionLevel: "edit",
        },
        {
            label: "Award Classifications",
            path: "/employees/award-classifications",
            icon: "📋",
            permissions: [],
            permissionLevel: "edit",
        },
        {
            label: "Departments",
            path: "/employees/departments",
            icon: "🏢",
            permissions: [],
            permissionLevel: "edit",
        },
        {
            label: "Teams",
            path: "/employees/teams",
            icon: "👥",
            permissions: [],
            permissionLevel: "edit",
        },
        {
            label: "Notification Rules",
            path: "/employees/notification-rules",
            icon: "🔔",
            permissions: [],
            permissionLevel: "edit",
        },
        {
            label: "Incident Notifications",
            path: "/employees/incident-notification-rules",
            icon: "⚠️",
            permissions: [],
            permissionLevel: "delete",
        }
    ],

    // Permissions - enforced server-side via employeesPermissions in the backend resolvers
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // Quick Actions
    quickActions: [
        {
            label: "Add Employee",
            path: "/employees/new",
            icon: "➕",
            description: "Register a new employee in the system"
        },
        {
            label: "View All",
            path: "/employees",
            icon: "👥",
            description: "View all employees"
        },
        {
            label: "Bulk Upload",
            path: "/employees/bulk-upload",
            icon: "📤",
            description: "Import multiple employees from CSV"
        }
    ]
};

export default employeesModuleConfig;